import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Accounts from "./pages/Accounts";
import Categories from "./pages/Categories";
import GoalsPage from "./pages/GoalsPage";
import ReportsPage from "./pages/ReportsPage";
import CreditCards from "./pages/CreditCards";

import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import { ProfileModalProvider } from "@/contexts/ProfileModalContext";
import { ProfileModal } from "@/components/forms/ProfileModal";
import { PWAInstallButton } from "@/components/common/PWAInstallButton";
import { PWAUpdateToast } from "@/components/common/PWAUpdateToast";
import { DataRefreshProvider } from "@/contexts/DataRefreshContext";


const queryClient = new QueryClient();

import { AnimatePresence } from "framer-motion";
import { PageTransition } from "./components/layout/PageTransition";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <TooltipProvider>
        <DataRefreshProvider>
          <ProfileModalProvider>
            <Sonner />
            <BrowserRouter>
              <RouteGuard />
              <AnimatePresence mode="wait">
                <Routes>
                  <Route path="/" element={<PageTransition><Index /></PageTransition>} />
                  <Route path="/accounts" element={<PageTransition><Accounts /></PageTransition>} />
                  <Route path="/categories" element={<PageTransition><Categories /></PageTransition>} />

                  <Route path="/goals" element={<PageTransition><GoalsPage /></PageTransition>} />
                  <Route path="/reports" element={<PageTransition><ReportsPage /></PageTransition>} />
                  <Route path="/credit-cards" element={<Navigate to="/?tab=credit-cards" replace />} />


                  <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
                  <Route path="/auth/reset" element={<PageTransition><ResetPassword /></PageTransition>} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
                </Routes>
              </AnimatePresence>
              <ProfileModal />
              <PWAInstallButton variant="banner" />
              <PWAUpdateToast />
            </BrowserRouter>
          </ProfileModalProvider>
        </DataRefreshProvider>
      </TooltipProvider>
    </ThemeProvider>
    <Analytics />
    <SpeedInsights />
  </QueryClientProvider>
);

export default App;

function RouteGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    const type = searchParams.get("type") || hashParams.get("type");
    const accessToken = hashParams.get("access_token") || searchParams.get("access_token");
    const code = searchParams.get("code") || hashParams.get("code");
    const shouldRedirect = type === "recovery" || !!accessToken || !!code;
    if (shouldRedirect && location.pathname !== "/auth/reset") navigate("/auth/reset" + location.search + location.hash, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
  return null;
}
